const isEmpty = (val) => val === null || val === undefined || val === '';

export const normalizeSegmentationType = (value, fallback = 'SIZEWISE') => {
	const normalized = String(value || '')
		.replace(/[\s_-]/g, '')
		.toUpperCase();
	return ['SIZEWISE', 'COLORWISE', 'DESIGNWISE'].includes(normalized) ? normalized : fallback;
};

export const getAutomationBuyerPriceValues = ({
	colorList,
	buyerQuotationType,
	colorWiseBuyerPrice,
	sizeWiseBuyerPrice,
	designWiseBuyerPrice
}) => {
	const safeColorList = Array.isArray(colorList) ? colorList : [];
	const sizeWiseFromSizes = {};
	const colorWiseFromSizes = {};
	let firstAvailablePrice = null;

	const getValidPrice = (value) => {
		const price = Number(value);
		return Number.isFinite(price) && price > 0 ? price : null;
	};

	safeColorList.forEach((color) => {
		let colorPrice = null;

		(color?.sizeQuantityPairList || []).forEach((size) => {
			const validPrice = getValidPrice(size?.price);
			if (validPrice === null) return;

			if (size?.id && sizeWiseFromSizes[size.id] === undefined) {
				sizeWiseFromSizes[size.id] = validPrice;
			}
			if (colorPrice === null) colorPrice = validPrice;
			if (firstAvailablePrice === null) firstAvailablePrice = validPrice;
		});

		if (color?.id && colorPrice !== null) colorWiseFromSizes[color.id] = colorPrice;
	});

	const normalizedType = normalizeSegmentationType(buyerQuotationType);

	return {
		normalizedBuyerQuotationType: normalizedType,
		colorWiseBuyerPrice: {
			...colorWiseFromSizes,
			...(colorWiseBuyerPrice || {})
		},
		sizeWiseBuyerPrice: {
			...sizeWiseFromSizes,
			...(sizeWiseBuyerPrice || {})
		},
		designWiseBuyerPrice:
			designWiseBuyerPrice ?? (normalizedType === 'DESIGNWISE' ? firstAvailablePrice : null)
	};
};

export const getOrderStylesGroupData = (styles = []) => {
	return styles.reduce((acc, item) => {
		const groupIdentifier = item?.productId ?? item?.productReferenceNumber;
		let group = acc.find((existingGroup) => existingGroup.groupIdentifier === groupIdentifier);

		if (!group) {
			group = {
				groupIdentifier,
				productReferenceNumber: item?.productReferenceNumber,
				name: item?.name,
				poData: []
			};
			acc.push(group);
		}

		group.poData.push({
			productId: item?.productId,
			productReferenceNumber: item?.productReferenceNumber,
			...item
		});

		return acc;
	}, []);
};

export const hasValidUnitPrice = (style) => {
	if (!style || typeof style !== 'object') return false;
	if (style.buyerQuotationType !== 'DESIGNWISE') return true;
	return !isEmpty(style?.designWiseBuyerPrice);
};

export const getFirstUnitPriceInvalidStyle = (orderStyles) => {
	if (!Array.isArray(orderStyles)) return null;

	return orderStyles.find((style) => !hasValidUnitPrice(style)) || null;
};

export const validateOrderStyles = (orderStyles) => {
	const missingProgramTypes = {};
	const missingSalesMargins = {};

	if (!Array.isArray(orderStyles)) return { missingProgramTypes, missingSalesMargins };

	orderStyles.forEach((item) => {
		if (!item.programTypeId && !item.programType?.id) {
			missingProgramTypes[item.id] = 'Program type is mandatory';
		}
		if (isEmpty(item.salesMarginPercentage) && isEmpty(item.salesMarginFobAmount)) {
			missingSalesMargins[item.id] = 'Sales margin is mandatory';
		}
	});

	return { missingProgramTypes, missingSalesMargins };
};

export const handleValidationErrors = ({
	missingProgramTypes,
	missingSalesMargins,
	setProgramTypeErrors,
	setSalesMarginErrors,
	dispatch,
	closeLoader,
	setIsPlacingOrder,
	toastError
}) => {
	const abort = (setError, errors) => {
		setError(errors);
		dispatch(closeLoader());
		setIsPlacingOrder(false);
		toastError('Please fill all required fields');
		return true;
	};

	if (Object.keys(missingProgramTypes).length > 0) return abort(setProgramTypeErrors, missingProgramTypes);
	setProgramTypeErrors({});

	if (Object.keys(missingSalesMargins).length > 0) return abort(setSalesMarginErrors, missingSalesMargins);
	setSalesMarginErrors({});

	return false;
};

export const validateHeaderFields = ({ basicInfoData }) => {
	const buyerId = basicInfoData?.selectedBuyer?.id;

	const projectManagerId = basicInfoData?.selectedProjectManager?.value;

	const accountManagerId = basicInfoData?.selectedAccountManager?.value;

	const revenueStream =
		basicInfoData?.selectedSalesChannel?.id || basicInfoData?.selectedSalesChannel?.value;

	if (!buyerId) return 'Buyer is required';
	if (!projectManagerId) return 'Project Manager is required';
	if (!accountManagerId) return 'Account Manager is required';
	if (!revenueStream) return 'Revenue stream is required';

	return null;
};
